import axios from "axios";
import { AuthContext } from "../../../context/AuthContext";
import { useState, useEffect, useContext, useMemo } from "react";
import { Table, Button, Typography, Select, Row, Col, Input } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import dayjs from "dayjs";

const { Title } = Typography;

export default function LateEarlyReport() {
    const { authData, backUrl } = useContext(AuthContext);

    const [loading, setLoading] = useState(false);
    const [attendanceData, setAttendanceData] = useState([]);

    const [organisations, setOrganisations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);

    const [selectedOrg, setSelectedOrg] = useState(null);
    const [selectedDept, setSelectedDept] = useState(null);
    const [selectedDesig, setSelectedDesig] = useState(null);

    const [searchText, setSearchText] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    useEffect(() => {
        fetchOrganisations();
        fetchLateEarlyRecords();
    }, []);

    const fetchOrganisations = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations`, {
                headers: { Authorization: `Bearer ${authData?.access}` }
            });
            setOrganisations(res.data.results || []);
        } catch (error) {
            console.error("Error fetching organisations:", error);
        }
    };

    const fetchDepartments = async (orgId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/departments/?organisation=${orgId}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            setDepartments(res.data.results || []);
        } catch (error) {
            console.error("Error fetching departments:", error);
            setDepartments([]);
        }
    };

    const fetchDesignations = async (deptId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/designations/?department=${deptId}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            setDesignations(res.data.results || []);
        } catch (error) {
            console.error("Error fetching designations:", error);
            setDesignations([]);
        }
    };

    const fetchLateEarlyRecords = async () => {
        try {
            setLoading(true);
            const res = await axios.get(
                `${backUrl}/api/hr/reports/attendance/`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );

            const rawData = res.data.data || [];

            // Filter only records with Late-In OR Early-Out
            const filtered = rawData.filter(item => {
                const late = item.late_arrival_duration && item.late_arrival_duration !== "00:00";
                const early = item.early_checkout_duration && item.early_checkout_duration !== "00:00";
                return late || early;
            });

            setAttendanceData(filtered);
        } catch (error) {
            console.error("Error fetching late/early records:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleOrgChange = (value) => {
        setSelectedOrg(value);
        setSelectedDept(null);
        setSelectedDesig(null);
        setDepartments([]);
        setDesignations([]);
        setCurrentPage(1);
        if (value) fetchDepartments(value);
    };

    const handleDeptChange = (value) => {
        setSelectedDept(value);
        setSelectedDesig(null);
        setDesignations([]);
        setCurrentPage(1);
        if (value) fetchDesignations(value);
    };

    const handleDesigChange = (value) => {
        setSelectedDesig(value);
        setCurrentPage(1);
    };

    const handleSearchChange = (e) => {
        setSearchText(e.target.value);
        setCurrentPage(1);
    };

    const tableData = useMemo(() => {
        return attendanceData.map((item) => {
            const dateObj = dayjs(item.date);

            return {
                key: item.id,

                // IDs / Filters (if needed later)
                organisationId: "-", // not in response
                departmentId: item.department || "-",
                designationId: item.designation || "-",

                // Grouping / Display
                monthYear: dateObj.isValid() ? dateObj.format("MMMM-YYYY") : "-",
                empId: item.emp_code || "-",
                empName: item.employee_name || "-",
                designationName: item.designation || "-",
                departmentName: item.department || "-",
                site: "Head Office", // static fallback

                // Attendance
                date: dateObj.isValid() ? dateObj.format("DD-MM-YYYY") : "-",
                inTime: item.first_check_in
                    ? dayjs(item.first_check_in).format("HH:mm")
                    : "-",
                outTime: item.last_check_out
                    ? dayjs(item.last_check_out).format("HH:mm")
                    : "-",
                totalHours: item.formatted_total_time || "-",
                lateInDuration: item.late_arrival_duration || "00:00",
                earlyOutDuration: item.early_checkout_duration || "00:00",
                totalDuration: item.formatted_total_time || "-",

                // Status / Remarks
                status: item.status || "-",
                reasonRemarks: item.status_message || "-",

                // Approval (not available in response)
                verifiedBy: "-",
                approvedBy: "-",

                // Extra (optional)
                shiftTimings: item.shift_timings || "-"
            };
        });
    }, [attendanceData]);


    const filteredTableData = useMemo(() => {
        return tableData.filter(row => {
            if (selectedOrg && row.organisationId !== selectedOrg) return false;
            if (selectedDept && row.departmentId !== selectedDept) return false;
            if (selectedDesig && row.designationId !== selectedDesig) return false;

            if (searchText.trim()) {
                const lower = searchText.toLowerCase().trim();
                return (
                    row.empId.toLowerCase().includes(lower) ||
                    row.empName.toLowerCase().includes(lower)
                );
            }
            return true;
        });
    }, [tableData, selectedOrg, selectedDept, selectedDesig, searchText]);

    const columns = [
        {
            title: "S.No",
            render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
            width: 70,
            fixed: "left"
        },
        { title: "Month–Year", dataIndex: "monthYear", width: 120 },
        { title: "Employee ID", dataIndex: "empId", width: 120 },
        { title: "Employee Name", dataIndex: "empName", width: 180 },
        { title: "Designation", dataIndex: "designationName", width: 160 },
        { title: "Department", dataIndex: "departmentName", width: 160 },
        { title: "Site/Location", dataIndex: "site", width: 140 },
        { title: "Date", dataIndex: "date", width: 120 },
        { title: "In Time", dataIndex: "inTime", width: 100 },
        { title: "Out Time", dataIndex: "outTime", width: 100 },
        { title: "Total Hours", dataIndex: "totalHours", width: 120 },
        { title: "Late-In Duration", dataIndex: "lateInDuration", width: 140 },
        { title: "Early-Out Duration", dataIndex: "earlyOutDuration", width: 140 },
        { title: "Total Duration", dataIndex: "totalDuration", width: 140 },
        { title: "Reason/Remarks", dataIndex: "reasonRemarks", width: 220 },
        // { title: "Verified By", dataIndex: "verifiedBy", width: 140 },
        // { title: "Approved By", dataIndex: "approvedBy", width: 140 },
    ];

    const downloadExcel = () => {
        const excelData = filteredTableData.map((row, index) => ({
            "S.No": index + 1,
            "Month–Year": row.monthYear,
            "Employee ID": row.empId,
            "Employee Name": row.empName,
            "Designation": row.designationName,
            "Department": row.departmentName,
            "Site/Location": row.site,
            "Date": row.date,
            "In Time": row.inTime,
            "Out Time": row.outTime,
            "Total Hours": row.totalHours,
            "Late-In Duration": row.lateInDuration,
            "Early-Out Duration": row.earlyOutDuration,
            "Total Duration": row.totalDuration,
            "Reason/Remarks": row.reasonRemarks,
            "Verified By": row.verifiedBy,
            "Approved By": row.approvedBy,
        }));

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Late Early Report");

        // Style header row
        const range = XLSX.utils.decode_range(worksheet["!ref"]);
        for (let C = range.s.c; C <= range.e.c; ++C) {
            const address = XLSX.utils.encode_cell({ r: 0, c: C });
            if (worksheet[address]) {
                worksheet[address].s = {
                    fill: { fgColor: { rgb: "0e497b" } },
                    font: { color: { rgb: "FFFFFF" }, bold: true },
                    alignment: { horizontal: "center" }
                };
            }
        }

        const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        saveAs(new Blob([buffer]), "Late_In_Early_Out_Report.xlsx");
    };

    return (
        <>
            <Title level={4}>Late In & Early Out Report</Title>

            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={5}>
                    <Select
                        placeholder="Select Organisation"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        value={selectedOrg}
                        onChange={handleOrgChange}
                        style={{ width: "100%" }}
                    >
                        {organisations.map(org => (
                            <Select.Option key={org.id} value={org.id}>
                                {org.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Col>

                <Col span={5}>
                    <Select
                        placeholder="Select Department"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        disabled={!selectedOrg}
                        value={selectedDept}
                        onChange={handleDeptChange}
                        style={{ width: "100%" }}
                    >
                        {departments.map(dept => (
                            <Select.Option key={dept.id} value={dept.id}>
                                {dept.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Col>

                <Col span={5}>
                    <Select
                        placeholder="Select Designation"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        disabled={!selectedDept}
                        value={selectedDesig}
                        onChange={handleDesigChange}
                        style={{ width: "100%" }}
                    >
                        {designations.map(des => (
                            <Select.Option key={des.id} value={des.id}>
                                {des.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Col>

                <Col span={5}>
                    <Input
                        placeholder="Search by Employee ID or Name"
                        allowClear
                        value={searchText}
                        onChange={handleSearchChange}
                        style={{ width: "100%" }}
                    />
                </Col>

                <Col span={4} style={{ textAlign: "right" }}>
                    <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={downloadExcel}
                        style={{ backgroundColor: "#0e497b" }}
                    >
                        Download Excel
                    </Button>
                </Col>
            </Row>

            <Table
                columns={columns}
                dataSource={filteredTableData}
                loading={loading}
                bordered
                pagination={{
                    current: currentPage,
                    pageSize,
                    total: filteredTableData.length,
                    onChange: page => setCurrentPage(page),
                    showSizeChanger: false
                }}
                scroll={{ x: "max-content" }}
                components={{
                    header: {
                        cell: props => (
                            <th {...props} style={{ background: "#0e497b", color: "#fff", textAlign: "center" }}>
                                {props.children}
                            </th>
                        )
                    }
                }}
            />
        </>
    );
}